/**
 * Clases, grilla, reservas y asistencia — AF §5 (RN-CLA-01..42).
 *
 * Fuente única de los esquemas que comparten backend, SPA web y app de socios.
 * Lo que la app necesita literal (`bookability`, la unión discriminada del check-in)
 * vive acá para que las dos puntas hablen el mismo idioma
 * (`viva-app/docs/api-needs.md` §4).
 */
import { z } from "zod";
import { dateISO, dateOnly, listQuery, moneySchema, objectId, reasonRequired, timeHHmm, yearMonth, } from "./base.js";
// ── Grilla mensual (RN-CLA-05..11) ───────────────────────────────────────────
export const createScheduleBody = z.object({
    branchId: objectId,
    month: yearMonth,
    /** RN-CLA-06: clonar el mes anterior y ajustar. */
    cloneFromMonth: yearMonth.optional(),
});
export const listSchedulesQuery = listQuery.extend({
    month: yearMonth.optional(),
    status: z.enum(["draft", "published"]).optional(),
});
/** RN-CLA-01: disciplina + profe + salón + día/hora + cupo. No hay clase sin salón. */
export const createScheduledClassBody = z.object({
    disciplineId: objectId,
    teacherId: objectId,
    roomId: objectId,
    weekday: z.number().int().min(0).max(6),
    startTime: timeHHmm,
    /** Si se omite (o va `null`), la clase dura lo que dice su disciplina (PV-111). */
    durationMin: z.number().int().min(5).max(300).nullable().optional(),
    /** RN-CLA-02: si se omite, hereda el cupo del salón para esa disciplina. */
    capacity: z.number().int().min(1).optional(),
    /** RN-CLA-39: tope opcional de visitantes; `null` = sin tope. */
    maxVisitors: z.number().int().min(0).nullable().optional(),
});
export const updateScheduledClassBody = createScheduledClassBody.partial().extend({
    /** Cambiar una clase ya publicada notifica a fijos e inscriptos (RN-CLA-07). */
    reason: z.string().trim().max(500).optional(),
});
/** RN-CLA-10: qué se dicta cada feriado del mes. */
export const holidayDecisionsBody = z.object({
    decisions: z.array(z.object({
        date: dateOnly,
        locationId: objectId.optional(),
        classesHeld: z.array(objectId).default([]),
    })),
});
/**
 * RN-CLA-10: los feriados de UN mes en el calendario del club (`settings.holidays`). La
 * lista reemplaza la del mes; las fechas tienen que ser de ese mes (lo valida la ruta).
 */
export const holidayCalendarMonthParams = z.object({ month: yearMonth });
export const holidayCalendarMonthBody = z.object({
    dates: z.array(dateOnly).max(31),
});
// ── Sesiones (RN-CLA-07..10, RN-CLA-32) ──────────────────────────────────────
export const listSessionsQuery = listQuery.extend({
    from: dateISO.optional(),
    to: dateISO.optional(),
    locationId: objectId.optional(),
    disciplineId: objectId.optional(),
    teacherId: objectId.optional(),
    scheduledClassId: objectId.optional(),
    status: z.string().optional(),
});
/** RN-CLA-08: la cancelación de una fecha es SIEMPRE manual y con motivo (D5). */
export const cancelSessionBody = z.object({
    reason: reasonRequired,
    /** RN-CLA-10/25: acreditar recuperos a los socios con límite sobre la disciplina. */
    grantRecoveryCredits: z.boolean().default(true),
});
export const substituteTeacherBody = z.object({
    substituteTeacherId: objectId.nullable(),
    reason: reasonRequired,
});
// ── Reservas (RN-CLA-12..17) ─────────────────────────────────────────────────
/**
 * Por qué el socio puede o no puede reservar. La app muestra `detail` tal cual
 * (`api-needs.md` §4): los textos los escribe el backend, en rioplatense.
 */
export const BOOKABILITY_REASONS = [
    "debt",
    "medical_expired",
    "frozen",
    "admission_banned",
    "sanction",
    "class_blocked",
    "overlap",
    "weekly_limit",
    /** RN-PLA-35: la actividad a elección ya tiene su clase de ese día. */
    "daily_limit",
    "visitor_no_extras",
    "plan_not_covered",
    "outside_horizon",
    "already_started",
    "canceled",
    "full",
    "blocked",
    "already_booked",
    "not_a_member",
    "boot_size_unavailable",
];
export const bookabilitySchema = z.object({
    canBook: z.boolean(),
    reason: z.enum(BOOKABILITY_REASONS).nullable(),
    detail: z.string().nullable(),
    /** RN-CLA-24: la reserva excede el límite semanal y hay que pagar el extra. */
    requiresExtra: z.boolean(),
    /** Precio del extra en centavos (lista vigente). `null` = no aplica. */
    extraPrice: moneySchema.nullable(),
    canJoinWaitlist: z.boolean(),
    canRequestFixed: z.boolean(),
    /** Sub-cupos de equipamiento (ej. Kangoo) */
    requiresBootSize: z.boolean().optional(),
    availableBootSizes: z.record(z.string(), z.number()).optional(),
    /** Advertencia física / arnés (ej. Bungee) */
    requiresWeightWarning: z.boolean().optional(),
    weightWarningText: z.string().nullable().optional(),
});
export const createBookingBody = z.object({
    sessionId: objectId,
    /**
     * RN-CLA-25: pedido explícito de usar un crédito de recupero. En la práctica **el
     * crédito se aplica solo** cuando la reserva pasa el tope de la semana —para eso se
     * acreditó—, así que el flag no cambia el resultado; queda por claridad de la app.
     */
    useRecoveryCredit: z.boolean().default(false),
    /** RN-CLA-24: aceptar el extra (crea el charge y deja la reserva pendiente de pago). */
    acceptExtra: z.boolean().default(false),
    /** Talle de bota para clases como Kangoo Jumps (S, M, L o "own" si trae propias). */
    bootSize: z.enum(["S", "M", "L", "own"]).optional(),
    /** Aceptación expresa de la advertencia de peso para Bungee. */
    weightWarningAcknowledged: z.boolean().default(false),
});
/** RN-CLA-15: recepción reserva en nombre del socio; queda el operador. */
export const createBookingForPersonBody = createBookingBody.extend({
    person: z.object({ kind: z.enum(["member", "visitor"]), id: objectId }),
    /** Cobrar el extra en mostrador en el mismo acto (RN-CLA-24). */
    chargeExtraAtCounter: z.boolean().default(false),
    paymentMethod: z.enum(["cash", "payway_terminal", "mp_point", "mp_qr", "transfer"]).optional(),
    shiftId: objectId.optional(),
});
export const cancelBookingQuery = z.object({
    /** Sólo calcula: no cancela. Es el «va a contar como falta» que la app anticipa. */
    preview: z.coerce.boolean().default(false),
});
// ── Fijos (RN-CLA-18..22bis) ─────────────────────────────────────────────────
export const requestFixedSlotBody = z.object({
    scheduledClassId: objectId,
    /** Recepción/coordinación pidiendo por un socio (RN-CLA-15). */
    memberId: objectId.optional(),
});
export const grantFixedSlotBody = z.object({
    reason: z.string().trim().max(500).optional(),
});
/** RN-CLA-20: quitar una fijación a mano es excepción D5 — motivo obligatorio. */
export const dropFixedSlotBody = z.object({ reason: reasonRequired });
// ── Lista de espera (RN-CLA-27..29) ──────────────────────────────────────────
export const joinWaitlistBody = z.object({
    /** Deja constancia de que acepta pagar el extra si el lugar aparece (RN-CLA-27). */
    acceptExtra: z.boolean().default(false),
});
export const confirmWaitlistOfferBody = z.object({
    useRecoveryCredit: z.boolean().default(false),
    acceptExtra: z.boolean().default(false),
    /** Confirmar un lugar de Kangoo pide el talle igual que reservar (`bookability.requiresBootSize`). */
    bootSize: z.enum(["S", "M", "L", "own"]).optional(),
    /** Confirmar un lugar de Bungee pide aceptar el aviso de peso igual que reservar. */
    weightWarningAcknowledged: z.boolean().default(false),
});
// ── Asistencia (RN-CLA-30..32bis) ────────────────────────────────────────────
/** RN-CLA-30: el socio escanea el QR del salón desde la app. */
export const checkInBody = z.object({ roomQr: z.string().trim().min(8).max(200) });
export const CHECKIN_REJECT_CODES = [
    "outside_window",
    "tolerance_expired",
    "no_capacity",
    "no_venue_entry",
    "invalid_qr",
    "weekly_limit",
    "blocked",
    "session_canceled",
];
/** Unión discriminada: el check-in no responde 4xx, responde por qué (api-needs §4). */
export const checkInResult = z.discriminatedUnion("ok", [
    z.object({
        ok: z.literal(true),
        outcome: z.enum(["checked_in", "walk_in", "already_present"]),
        bookingId: objectId,
        session: z.record(z.string(), z.unknown()),
        message: z.string(),
    }),
    z.object({
        ok: z.literal(false),
        code: z.enum(CHECKIN_REJECT_CODES),
        message: z.string(),
        action: z.object({
            kind: z.enum(["pay", "upload_certificate", "open_class", "none"]),
        }),
    }),
]);
/** RN-CLA-32: el profe marca presente a quien no pudo escanear, hasta el minuto 15. */
export const teacherMarkPresentBody = z.object({ bookingId: objectId });
export const teacherWalkInBody = z.object({
    person: z.object({ kind: z.enum(["member", "visitor"]), id: objectId }),
});
/** RN-CLA-32: consolidar y, en el mismo acto, dar los lugares de los ausentes. */
export const consolidateSessionBody = z.object({
    assignSpots: z.array(z.object({ kind: z.enum(["member", "visitor"]), id: objectId })).default([]),
});
/** RN-CLA-32: corrección posterior a la consolidación — coordinador, motivo, auditada. */
export const fixAttendanceBody = z.object({
    bookingId: objectId,
    status: z.enum(["present", "absent"]),
    reason: reasonRequired,
});
// ── Cancelación y penalidades (RN-CLA-33..35) ────────────────────────────────
export const cancelPreview = z.object({
    inTerm: z.boolean(),
    countsAsFault: z.boolean(),
    deadline: z.coerce.date(),
    /** Cuántas faltas quedarían en la ventana y si dispara sanción (RN-CLA-34). */
    faultsInWindow: z.number().int(),
    wouldTriggerPenalty: z.boolean(),
    penaltyUntil: z.coerce.date().nullable(),
    /** RN-CLA-22: si es un fijo, la falta cuenta para la caída de la fijación. */
    affectsFixedSlot: z.boolean(),
    message: z.string(),
});
/** RN-CLA-35: levantar una sanción es excepción de encargado+ con motivo. */
export const liftPenaltyBody = z.object({ reason: reasonRequired });
export const listPenaltiesQuery = listQuery.extend({
    personId: objectId.optional(),
    kind: z.enum(["booking_restriction", "fixed_slot_block"]).optional(),
    active: z.coerce.boolean().optional(),
});
// ── Recuperos (RN-CLA-25) ────────────────────────────────────────────────────
/** Compensación manual del coordinador: motivo obligatorio, auditada (D5). */
export const grantRecoveryCreditBody = z.object({
    memberId: objectId,
    disciplineId: objectId,
    weekOf: dateOnly.optional(),
    reason: reasonRequired,
});
// ── Clase de prueba (RN-CLA-17, RN-PLA-22) ───────────────────────────────────
export const createTrialClassBody = z.object({
    dni: z.string().trim().min(6).max(15),
    person: z.object({
        name: z.string().trim().min(2).max(120),
        age: z.number().int().min(3).max(110).optional(),
        phone: z.string().trim().max(40).optional(),
        email: z.string().trim().email().optional(),
    }),
    sessionId: objectId,
    prospectId: objectId.optional(),
});
export const setTrialAttendedBody = z.object({ attended: z.boolean() });
// ── Eventos (RN-CLA-41/42) ───────────────────────────────────────────────────
export const createEventBody = z.object({
    kind: z.enum(["master", "viva_games"]),
    name: z.string().trim().min(2).max(160),
    description: z.string().trim().max(2000).default(""),
    date: dateISO,
    branchId: objectId,
    locationId: objectId,
    capacity: z.number().int().min(1),
    /** PV-30: precio configurable ≥ $0. */
    price: moneySchema.min(0),
    teamMode: z.boolean().default(false),
    /** Se liquida como hora normal del profe: la sesión se crea si viene el profe. */
    teacherId: objectId.optional(),
    roomId: objectId.optional(),
    durationMin: z.number().int().min(15).max(600).default(60),
});
export const updateEventBody = createEventBody.partial().omit({ branchId: true });
export const registerToEventBody = z.object({
    person: z.object({ kind: z.enum(["member", "visitor"]), id: objectId }).optional(),
    team: z
        .object({
        name: z.string().trim().min(2).max(120),
        memberIds: z.array(objectId).default([]),
    })
        .optional(),
    /** Cobro en mostrador (recepción/kiosko) — RN-CLA-42. */
    payAtCounter: z.boolean().default(false),
    paymentMethod: z.enum(["cash", "payway_terminal", "mp_point", "mp_qr", "transfer"]).optional(),
    shiftId: objectId.optional(),
});
/** RN-CLA-42: cancelar el evento devuelve las inscripciones (auditado, D5). */
export const cancelEventBody = z.object({ reason: reasonRequired });
// ── App de socios (api-needs.md §4) ──────────────────────────────────────────
export const appGridQuery = z.object({
    branchId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    disciplineId: objectId.optional(),
});
// ── Reportes (Pantalla «Reporte de ocupación y asistencia») ──────────────────
export const occupancyReportQuery = z.object({
    branchId: objectId.optional(),
    locationId: objectId.optional(),
    disciplineId: objectId.optional(),
    teacherId: objectId.optional(),
    month: yearMonth.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    groupBy: z.enum(["class", "discipline", "teacher", "slot", "branch"]).default("class"),
    format: z.enum(["json", "csv"]).default("json"),
});
//# sourceMappingURL=clases.js.map